/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { BaseContainerComponent } from './base-container.component';
import { zip } from 'rxjs';

export enum ContainerType {
	MultiSelectMenu = 'multiSelectMenu',
	UndoPanel = 'undo',
	HoverOver = 'hoverOver',
}

@Injectable()
/**
 * This class manages the z-indices of components on screen, e.g. draggable containers.
 * It uses a map with z-index number as key and value being an array of components at this z-index
 * Each component is identified by an unique containerType
 *
 * When a component is clicked on, array of given z-index is sorted by when they were last interacted with
 * Components in the array will be given z-indices of lowest of designated z-index, and increment by 1
 * Say, both multi-select-menu and undo panel are positioned with z-index of 10001
 * when multi-select-menu is dragged, its z-index becomes 10002 and undo will be 10001
 */
export class ContainerLayerManager {
	private zIndexMap: Map<number, BaseContainerComponent[]>;

	constructor() {
		this.zIndexMap = new Map<number, BaseContainerComponent[]>();
	}

	/**
	 * Register the component so it can be managed
	 *
	 * @param container instance of container to be managed
	 * @param type contain type
	 * @param zIndex z-index of component
	 */
	public register(container: BaseContainerComponent, type: ContainerType, zIndex: number = 10001): void {
		// don't have key of specified z-index, created new k-v pair
		if (!this.zIndexMap.get(zIndex)) {
			this.zIndexMap.set(zIndex, [container]);
		} else {
			// already have the same z-index as key, conditionally add to the value array if it doesn't already exist
			const arr = this.zIndexMap.get(zIndex);
			if (arr.filter((item) => item.getContainerType() === type).length === 0) {
				arr.push(container); // push to end so it forces to reorder in bringToTop()
			}
		}
		this.bringToTop(type, zIndex);
	}

	/**
	 * Unregister the component
	 *
	 * @param type container type
	 * @param zIndex z-index of component
	 */
	public unregister(type: ContainerType, zIndex: number): void {
		if (!this.zIndexMap.get(zIndex)) {
			return;
		}

		const arr = this.zIndexMap.get(zIndex);
		for (let i = 0; i < arr.length; i++) {
			if (arr[i].getContainerType() === type) {
				arr.splice(i, 1);
				break;
			}
		}
	}

	/**
	 * Bring the interacted component to top
	 *
	 * @param type container type
	 * @param zIndex z-index of component
	 */
	public bringToTop(type: ContainerType, zIndex: number): void {
		const arr = this.zIndexMap.get(zIndex);

		// skip ordering if there's only one element at given z-index or frontmost component matches given type
		// and if this is not hover-over container
		if (
			(!arr || arr.length === 1 || arr[0].getContainerType() === type) &&
			// Seems like this code works only for hover-over container with custom zIndex, which is why this check extended to handle this exceptional case
			type !== ContainerType.HoverOver
		) {
			return;
		}

		const highestIndex = zIndex + arr.length - 1;
		let index = highestIndex;
		let item: BaseContainerComponent;
		let containerIndex = 0;

		for (let i = 0; i < arr.length; i++) {
			item = arr[i];
			if (item.getContainerType() === type) {
				item.setZIndex(highestIndex);
				containerIndex = i;
			} else {
				item.setZIndex(--index);
			}
		}

		arr.unshift(arr.splice(containerIndex, 1)[0]);
	}
}
